import React from 'react';
import { useAuth0 } from '../contexts/useAuthContext';
import { Navigate } from 'react-router-dom';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import NotFound from '../pages/errors/404';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
  requireAdmin?: boolean;
  fallbackPath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRoles = [], 
  requireAdmin = false,
  fallbackPath = '/login'
}) => {
  const { isAuthenticated, isLoading, hasAnyRole, isAdmin, isSupport, userRole } = useAuth0();

  // Show loading indicator while authentication is in progress OR while role is still loading
  if (isLoading || (isAuthenticated && !userRole)) {
    return (
      <div className="loading-container mt-2">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Check for admin requirement — strict Admin only (Support users are excluded)
  if (requireAdmin && (!isAdmin() || isSupport())) {
    return <NotFound />;
  }

  // Check for specific role requirements
  if (requiredRoles.length > 0 && !hasAnyRole(requiredRoles)) {
    return <Navigate to="/" replace />;
  }

  // Check for unassigned role (optional - might want to allow or restrict)
  //if (userRole?.role === 'NotAssigned') {
  if (userRole?.role === 'NormalUser' && (!userRole.groups || userRole.groups.length === 0)) {
    let groupsDisplay = 'None assigned';
    if (Array.isArray(userRole.groups) && userRole.groups.length > 0) {
      groupsDisplay = userRole.groups.join(', ');
    }

    return (
      <div className="role-pending">
        <h2>Account Setup Pending</h2>
        <p>Your account has been authenticated, but role assignment is still pending.</p>
        <p>Please contact your administrator to assign appropriate permissions.</p>
        <p>
          Groups: {groupsDisplay}
        </p>
      </div>
    );
  }

  // Render the protected component if all checks pass
  return <>{children}</>;
};

// Higher-order component for admin-only routes
export const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <ProtectedRoute requireAdmin={true}>{children}</ProtectedRoute>;
};

// Higher-order component for specific role-based routes
export const RoleBasedRoute: React.FC<{ 
  children: React.ReactNode; 
  roles: string[] 
}> = ({ children, roles }) => {
  return <ProtectedRoute requiredRoles={roles}>{children}</ProtectedRoute>;
};

export default ProtectedRoute;
